import { useState, useCallback, useRef } from "react";
import "./Northrop.scss";
import northrop from "../../data/northrop.json";
import customerinfo from "../../data/customerinfo";
import branches from "../../data/branches.json";
//import vendorPricing from '../../data/vendor_pricing.json';
import Map, {
  Marker,
  Popup,
  NavigationControl,
  FullscreenControl,
  ScaleControl,
  GeolocateControl,
  Source,
  Layer,
  MapRef,
} from "react-map-gl";
import Pin from "../../assets/pin";
import "mapbox-gl/dist/mapbox-gl.css";
import { escapeString } from "../../helpers/escapeString";
import { downloadCsv } from "../../helpers/downloadCsv";
import { Header } from "../../components/Header";
import { ActionButtons } from "../../components/ActionButtons";
import { CardButton, TextInput } from "@fsg/gui-bits";
import * as turf from "@turf/turf";
import { Units } from "@turf/turf";

const northropData = northrop.filter(
  (item) => item["Latitude"] && item["Longitude"]
);

function NorthropPage({ publicView = false }) {
  const [selectedLocation, setSelectedLocation] = useState<any>(null);
  const [customerData] = useState<any>(northropData);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const map = useRef<MapRef>(null);

  document.title = `FSG ${publicView ? "(Public)" : ""} -  ${
    customerinfo.northrop.name
  } Coverage Map`;

  const exportData = useCallback(() => {
    let csvContent = "Building Address,City,State,Region"; //,V1,V1 Distance,V2,V2 Distance,V3,V3 Distance,V4,V4 Distance,V5,V5 Distance\n";
    customerData.forEach(function (row: any) {
      const rowArray = [
        row["Building Address"],
        row["City"],
        row["State"],
        row["Region"],
        // vendors[0]["Name"],
        // vendors[0]["distance"].toFixed(1),
        // vendors[1]["Name"],
        // vendors[1]["distance"].toFixed(1),
        // vendors[2]["Name"],
        // vendors[2]["distance"].toFixed(1),
        // vendors[3]["Name"],
        // vendors[3]["distance"].toFixed(1),
        // vendors[4]["Name"],
        // vendors[4]["distance"].toFixed(1),
      ];
      csvContent += rowArray.map(escapeString).join(",");
      csvContent += "\n";
    });

    downloadCsv(csvContent, "northrop");
  }, [customerData]);

  return (
    <div>
      <Header customer={customerinfo.northrop.name} />
      <div className="dashboard-content">
        <ActionButtons
          customerGuid={customerinfo.northrop.guid}
          publicView={publicView}
          exportData={exportData}
        />

        <div className="map-container">
          <div className="site-list">
            <div className="site-search">
              <TextInput
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search for a site"
              />
            </div>
            <div className="site-list-list">
              {customerData
                .filter((x: any) => {
                  if (searchTerm === "") {
                    return true;
                  }
                  return (
                    x["Building Address"]
                      .toLowerCase()
                      .includes(searchTerm.toLowerCase()) ||
                    x["City"]
                      .toLowerCase()
                      .includes(searchTerm.toLowerCase()) ||
                    `${x["Zip"]}`
                      .toLowerCase()
                      .includes(searchTerm.toLowerCase())
                  );
                })
                .map((item: any) => {
                  return (
                    <CardButton
                      key={item["Name"]}
                      className="site-listing"
                      onClick={() => {
                        map.current?.flyTo({
                          center: [item["Longitude"], item["Latitude"]],
                          zoom: 10,
                          duration: 1000,
                        });
                        setSelectedLocation(item);
                      }}
                    >
                      <h4>Property {item["Property ID"]}</h4>
                      <address>
                        <div>{item["Building Address"]}</div>
                        <div>
                          {item["City"]}, {item["State"]} {item["Zip"]}
                        </div>
                      </address>
                    </CardButton>
                  );
                })}
            </div>
          </div>
          <Map
            ref={map}
            mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
            initialViewState={{
              longitude: -95.7,
              latitude: 37.09,
              zoom: 3,
            }}
            maxZoom={15}
            style={{ width: "100%", height: "80vh", marginTop: "1rem" }}
            mapStyle="mapbox://styles/mapbox/streets-v9"
            onClick={() => {
              setSelectedLocation(null);
            }}
            onDrag={() => {
              setSelectedLocation(null);
            }}
          >
            <GeolocateControl position="top-left" />
            <FullscreenControl position="top-left" />
            <NavigationControl position="top-left" />
            <ScaleControl />

            <Source
              id="fsg"
              type="geojson"
              data={{
                type: "FeatureCollection",
                features: branches.map((item) => {
                  const center = [item["Longitude"], item["Latitude"]];
                  const options = {
                    units: "miles" as Units,
                  };
                  const circle = turf.circle(center, 50, options);
                  return circle;
                }),
              }}
            >
              <Layer
                type="fill"
                paint={{
                  "fill-color": "rgb(51,94,238)",
                  "fill-opacity": 0.2,
                }}
              />
            </Source>

            {customerData.map((item: any) => (
              <Marker
                key={item["Location ID"]}
                longitude={item["Longitude"]}
                latitude={item["Latitude"]}
                onClick={(e) => {
                  e.originalEvent.preventDefault();
                  e.originalEvent.stopPropagation();
                  if (selectedLocation) {
                    setSelectedLocation(null);
                  } else {
                    setSelectedLocation(item);
                  }
                }}
                anchor="bottom"
              >
                <Pin fill="#000000" />
              </Marker>
            ))}
            {selectedLocation && (
              <Popup
                anchor="top"
                longitude={selectedLocation["Longitude"]}
                latitude={selectedLocation["Latitude"]}
                onClose={() => {
                  setSelectedLocation(null);
                }}
                style={{ width: "400px", maxWidth: "400px" }}
              >
                <div>
                  <div className="selected-property">
                    <div>
                      <div>Region: {selectedLocation["Region"]}</div>
                      <div>Type: {selectedLocation["Primary Use Type"]}</div>
                      <div>
                        Gross Sq. Ft: {selectedLocation["Gross Sq. Ft"]}
                      </div>
                      <div>
                        Outsourced SF. Ft:{" "}
                        {selectedLocation["Electrical SF Outsourced"]}
                      </div>
                    </div>
                    <address>
                      <div>{selectedLocation["Building Address"]}</div>
                      <div>
                        {selectedLocation["City"]}, {selectedLocation["State"]}{" "}
                        {selectedLocation["Zip"]}
                      </div>
                    </address>
                  </div>
                </div>
              </Popup>
            )}
          </Map>
        </div>
      </div>
    </div>
  );
}

export default NorthropPage;
